// @flow
/* eslint flowtype/generic-spacing: 0 */
import { createSelector, type InputSelector } from "reselect"
import * as R from "ramda"

import { SERVICES as ADDITIONAL_SERVICES, SERVICES_STATUSES } from "mmb/consts/additionalServices"
import { BOOKING_STATUSES } from "mmb/consts/booking"
import {
  bookingDataSelector,
  bookingAdditionalServicesSelector,
} from "mmb/scenes/AccountBooking/components/Booking/services/selector"
import type { PendingAdditionalService } from "../additionalServicesTypes"
import { servicesSelector } from "common/services/branding/services"
import { promotionsSelector } from "common/services/branding/promotions"
import { additionalServicesToShowSelector } from "./additionalServicesToShowSelector"

/**
 * Determines which of the additional services should appear in AS Statuses
 */
export const servicesToShowStatusSelector: InputSelector<
  any,
  void,
  $ReadOnlyArray<PendingAdditionalService>,
> = createSelector(
  bookingAdditionalServicesSelector,
  services =>
    services
      .toJS()
      .filter(
        ({ category }) =>
          category !== ADDITIONAL_SERVICES.SCHEDULE_CHANGE &&
          category !== ADDITIONAL_SERVICES.PRICE_CHANGE &&
          category !== ADDITIONAL_SERVICES.PASSENGER_EDIT,
      ),
)

/**
 * Returns pending additional services filtered by status *open*
 */
export const additionalServicesToPaySelector: InputSelector<
  any,
  void,
  $ReadOnlyArray<PendingAdditionalService>,
> = createSelector(
  bookingAdditionalServicesSelector,
  services => services.toJS().filter(({ status }) => status === SERVICES_STATUSES.OPEN),
)

/**
 * Determines if the AS Tiles should be displayed.
 */
export const showAdditionalServicesTilesSelector: InputSelector<
  any,
  void,
  boolean,
> = createSelector(
  [bookingDataSelector, additionalServicesToShowSelector],
  (booking, servicesToShow) =>
    Boolean(servicesToShow.length) &&
    booking.status === BOOKING_STATUSES.CONFIRMED &&
    booking.journey.endsInFuture(),
)

/**
 * Determines whether the info text should be displayed in the AS Tiles.
 * Returns true if the booking status is *closed* (processing)
 */
export const showAdditionalServicesClosedInfoSelector: InputSelector<
  any,
  void,
  boolean,
> = createSelector(
  state => state.booking.data.status,
  R.equals(BOOKING_STATUSES.CLOSED),
)

/**
 * Determines whether the Additional Services card should be displayed on the main MMB page.
 * Returns true if at least one of the following selectors returns true:
 * <br><br>
 * *servicesToShowStatusSelector* || *showAdditionalServicesClosedInfoSelector* || *showAdditionalServicesTilesSelector*
 */
export const showAdditionalServicesSelector: InputSelector<any, void, boolean> = createSelector(
  [
    servicesToShowStatusSelector,
    showAdditionalServicesClosedInfoSelector,
    showAdditionalServicesTilesSelector,
  ],
  (statuses, showClosedInfo, showTiles) => Boolean(statuses.length) || showClosedInfo || showTiles,
)

export const servicesAndPromotionSelector: $FlowFixMe = createSelector(
  [servicesSelector, promotionsSelector],
  (services, promotions) => services.merge(promotions),
)

/**
 * Returns function which takes an optional param *serviceId*.
 * Calling the func with the param will return the corresponding pending AS.
 * If no param provided, it returns the first pending AS.
 */
export const getAdditionalServiceToPaySelector: InputSelector<
  any,
  void,
  (number) => ?PendingAdditionalService,
> = createSelector(
  additionalServicesToPaySelector,
  servicesToPay => serviceId => {
    if (R.isNil(serviceId)) {
      return servicesToPay[0]
    }
    return servicesToPay.find(s => s.id === serviceId)
  },
)

/**
 * Gets additional services to pay which are NOT shown in AS Tiles
 * Comparison is based on *service.id*
 */
export const servicesForBlackPaymentBannerSelector: InputSelector<
  any,
  void,
  $ReadOnlyArray<PendingAdditionalService>,
  // $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
> = createSelector(
  [additionalServicesToPaySelector, servicesToShowStatusSelector],
  R.differenceWith(R.eqProps("id")),
)

/**
 * Whether the black payment banner should be displayed
 * Depends on if `servicesForBlackPaymentBannerSelector` returns > 0 services
 */
export const showBlackPaymentBannerSelector: InputSelector<any, void, boolean> = createSelector(
  servicesForBlackPaymentBannerSelector,
  servicesForBlackPayment => Boolean(servicesForBlackPayment.length),
)

/**
 * Gets first additional services with status `created`
 */
export const createdAdditionalServiceToPaySelector: InputSelector<
  any,
  void,
  ?PendingAdditionalService,
> = createSelector(
  bookingAdditionalServicesSelector,
  additionalServices =>
    additionalServices.filter(x => x.status === SERVICES_STATUSES.CREATED).toJS()[0],
)

/**
 *  Memoized version with createSelector is not being rerun when additionalServices have been updated.
 *  So in the case of a payment failure, the service was being ordered (and paid for) a second time.
 */
export const createdAdditionalServiceToPaySelectorNonMemoized = (state: any) => {
  const { additionalServices } = bookingDataSelector(state)
  return additionalServices.filter(x => x.status === SERVICES_STATUSES.CREATED).toJS()[0]
}

export const servicePackageTypeSelector: $FlowFixMe = createSelector(
  bookingDataSelector,
  booking => booking.servicePackage && booking.servicePackage.servicePackageType,
)
